import { Text, Flex, SimpleGrid, Box, Tooltip, useDisclosure } from '@chakra-ui/react';
import { RaceParticipants } from 'api/generated/graphql';
import { ChartData } from 'chart.js';
import React from 'react';
import { IconInfo, IconTyreC1, IconTyreC2, IconTyreC3, IconTyreC4, IconTyreC5, IconTyreInter, IconTyreWet, IconWatch } from 'icons';
import { RBaseCardBottomLeft, RBaseCardBottomRight, RBaseCardTopLeft, RBaseCardTopRight, RCarCard, RCarGridCard, RPartCard } from 'components';
import RPerformanceCarCard from 'components/PerformanceCarCard/PerformanceCarCard';
import RSpiderChart from 'components/SpiderChart/SpiderChart';
import { AppliedLivery, CarTier, Livery, RarityTier, Tyres } from 'types';
import { formatOrdinal } from 'utils/helpers';
import GridCardHeader from 'components/GridCardHeader/GridCardHeader';
import RaceLevel from 'components/Races2/RaceLevel/RaceLevel';
import { rarityLevels } from 'utils/themeConstants';
import Flags, { FlagsProps } from 'components/flags';
import { AvatarPicture } from 'components/Avatar/AvatarPicture';
import { useUserSettings } from 'hooks';
import CustomTooltip from 'components/Tooltip/CustomTooltip';

interface CarPerformanceProps {
  player?: RaceParticipants | undefined;
  comparisonPlayer?: RaceParticipants | undefined;
  spiderData: ChartData<'radar', unknown, unknown>;
  currentPlayerId?: string;
  style: 'first' | 'comparisson';
  tipsForTyre: string;
}

export const CarPerformanceStats = ({
  player,
  spiderData,
  style,
  currentPlayerId,
  comparisonPlayer,
  tipsForTyre
}: CarPerformanceProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getUserSettings } = useUserSettings()

  const tyreIconLookup = {
    C1: IconTyreC1,
    C2: IconTyreC2,
    C3: IconTyreC3,
    C4: IconTyreC4,
    C5: IconTyreC5,
    INTER: IconTyreInter,
    WET: IconTyreWet,
  };

  const getTyreIcon = (TyreId: Tyres) => {
    const TyreIcon = tyreIconLookup[TyreId] || IconTyreC1;
    return <TyreIcon 
      w="2.5rem"
      h="auto"
    />;
  };


  return (
    <Flex w="full" flexDir="column">
      <Flex gap={4} flexDir="column">
        <Text
          fontSize={{ base: '1.125rem', md: '1.5rem' }}
          lineHeight={{ base: '1.5rem', md: '1.75rem' }}
          fontWeight={400}
          background="linear-gradient(180deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
          backgroundClip="text"
          textTransform="uppercase"
          fontFamily="heading"
        >
          COMPARE CAR SETUPS
        </Text>

        <Flex
          flexDir="column"
          gap="1rem"
        >
          <Flex
            flexDir="column"
          >
            {(player?.qualifyingGridPosition || 
              player?.position || 
              player?.user.id === getUserSettings?.data?.getUser.id) && (
              <GridCardHeader
                gridPosition={player?.position}
                ownCar={player?.user.id === getUserSettings?.data?.getUser.id}
                gridType={'results'}
                showAllResults={true}
                runnerUp
              />
            )}
            
            <Flex
              px={{ base: '3', md: '4' }}
              flexDir="column"
            >
              <RCarGridCard
                isResults={true}
                backgroundPreload
                playersParticipant={player}
                livery={player?.car?.livery as Livery}
                appliedLivery={player?.car?.appliedLivery as AppliedLivery}
                h="180px"
                imageProps={{
                  transform: 'scale(70%)',
                  marginBottom: '-1rem',
                }}  
                w="full"
                hasCardBg
                hasInsetBorder={false}
                style={{
                  backgroundOrigin: 'border-box',
                }}
              >
                <RBaseCardTopLeft>
                  <Flex
                    flexDirection="column"
                    gap="10px"
                    justifyContent="center"
                  >
                    <Flex alignItems="flex-start" gap="1" flexDir="column" mt="0.5rem">
                      <Text 
                        fontSize={{ base: '12px', md: '18px' }}
                        lineHeight={{ base: '16px', md: '24px' }}
                        fontFamily="heading" 
                        fontWeight="400"
                        color="white.95"
                      >
                        {player?.car?.name}
                      </Text>
                      <Flex alignItems="center" gap="1">
                        <Box>
                          <RaceLevel
                            level={rarityLevels[(player?.car?.rarity as RarityTier) as keyof typeof rarityLevels] || rarityLevels.common}
                            textFontSize="8px"
                            numberFontSize="12px"
                            minW="fit-content"
                          />
                        </Box>
                      </Flex>
                    </Flex>
                  </Flex>
                </RBaseCardTopLeft>
                <RBaseCardTopRight maxW="50%" flexDir="column">
                  <Flex
                    flexDir="column"
                    alignItems="center"
                    flex={1}
                  >
                    <Text
                      fontSize="0.625rem"
                      lineHeight="0.75rem"
                      fontWeight={600}
                      color="white.80"
                      textTransform="uppercase"
                      textAlign="center"
                    >
                      EARNINGS
                    </Text>
                    <Flex
                      background="linear-gradient(360deg, rgba(79, 197, 186, 0.2) 0%, rgba(79, 197, 186, 0) 100%)"
                      borderRadius="4px"
                      px="3"
                      py="2"
                      borderTop="none"
                      position="relative"
                      w="80px"
                      justifyContent="center"
                      _before={{
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "4px",
                        padding: "2px",
                        paddingTop: "0px",
                        borderWidth: "0px 1.5px 1.5px 1.5px",
                        background: "linear-gradient(360deg, rgba(79, 197, 186, 0.48) 0%, rgba(79, 197, 186, 0) 100%)",
                        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                        maskComposite: "exclude",
                      }}
                    >
                      <Text
                        fontSize="20px"
                        lineHeight="24px"
                        fontWeight={600}
                        color="treasuredTeal.100"
                      >
                        ${(player?.earnings || 0) / 100}
                      </Text>
                    </Flex>
                  </Flex>

                  <Flex
                    flexDir="column"
                    alignItems="center"
                    flex={1}
                  >
                    <Text
                      fontSize="0.625rem"
                      lineHeight="0.75rem"
                      fontWeight={600}
                      color="white.80"
                      textTransform="uppercase"
                      textAlign="center"
                    >
                      POINTS
                    </Text>
                    <Flex
                      background="linear-gradient(360deg, rgba(216, 56, 50, 0.2) 0%, rgba(216, 56, 50, 0) 100%)"
                      borderRadius="4px"
                      px="3"
                      py="2"
                      borderTop="none"
                      position="relative"
                      w="80px"
                      justifyContent="center"
                      _before={{
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "4px",
                        padding: "2px",
                        paddingTop: "0px",
                        borderWidth: "0px 1.5px 1.5px 1.5px",
                        background: "linear-gradient(360deg, rgba(216, 56, 50, 0.48) 0%, rgba(216, 56, 50, 0) 100%)",
                        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                        maskComposite: "exclude",
                      }}
                    >
                      <Text
                        fontSize="20px"
                        lineHeight="24px"
                        fontWeight={600}
                        color="bloodMoon.100"
                      >
                        {player?.points || 0}
                      </Text>
                    </Flex>
                  </Flex>
                </RBaseCardTopRight>
                <RBaseCardBottomRight>
                  <Flex alignItems="center" h="1.5rem" gap="2">
                    <Flags
                      countryCode={
                        player?.user
                          ?.countryFlag as FlagsProps['countryCode'] ||
                        'default'
                      }
                    />
                    <AvatarPicture
                      key={player?.user.avatarId}
                      avatarId={player?.user.avatarId || ''}
                    />
                    <Text 
                      fontSize="0.75rem"
                      lineHeight="120%"
                      fontFamily="heading" 
                      fontWeight="400"
                      color="white.95"
                      noOfLines={1}
                    >
                      {player?.user?.playerName || 'Owner Name'}
                    </Text>
                  </Flex>
                </RBaseCardBottomRight>
                <RBaseCardBottomLeft alignItems="center">
                  <Flex
                    flexDirection={{ base: 'column', md: 'row' }}
                    alignItems="center"
                    gap={4}
                    zIndex={2}
                    background="linear-gradient(225deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
                    borderTopRightRadius="12px"
                    borderTopLeftRadius="2px"
                    borderBottomLeftRadius="12px"
                    borderBottomRightRadius="2px"
                    px="2"
                    py="1.5"
                  >
                    <IconWatch
                    />
                  </Flex>

                  <Flex
                    flexDir="column"
                    alignItems="flex-start"
                  >
                    <Text
                      fontSize="0.75rem"
                      lineHeight="100%"
                      fontFamily="heading" 
                      fontWeight="400"
                      color="white.80"
                    >
                      TIME
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      lineHeight="0.875rem"
                      fontWeight="600"
                      color="white.60"
                    >
                      {player?.time || '00:00:00'}
                    </Text>
                  </Flex>
                </RBaseCardBottomLeft>
              </RCarGridCard>
              <Flex mt={2} alignItems="center" justifyContent="center" gap="2" position="relative">
                {getTyreIcon((player?.tyres?.id as Tyres) || 'C1')}

                <Text
                  fontSize={{ base: '1.125rem', md: '1.5rem' }}
                  lineHeight={{ base: '1.5rem', md: '1.75rem' }}
                  fontWeight={400}
                  color="white.80"
                  fontFamily="heading"
                >
                  {player?.tyres?.name}
                </Text>

                {player?.user?.id === getUserSettings?.data?.getUser.id && (
                  <CustomTooltip
                    label={tipsForTyre}
                    placement="top"
                    hasArrow
                    gutter={8}
                    isOpen={isOpen}
                  >
                    <Box 
                      display="inline-block"
                      onMouseEnter={onOpen}
                      onMouseLeave={onClose}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen();
                      }}
                    >
                      <IconInfo 
                        __css={{
                          path: {
                            fill: 'white.80'
                          }
                        }}
                      />
                    </Box>
                  </CustomTooltip>
                )}
              </Flex>
            </Flex>
          </Flex>

          <Flex
            flexDir="column"
          >
            {(comparisonPlayer?.qualifyingGridPosition || 
              comparisonPlayer?.position || 
              comparisonPlayer?.user.id === getUserSettings?.data?.getUser.id) && (
              <GridCardHeader
                gridPosition={comparisonPlayer?.position}
                ownCar={comparisonPlayer?.user.id === getUserSettings?.data?.getUser.id}
                gridType={'results'}
                showAllResults={true}
                runnerUp
              />
            )}
            
            <Flex
              px={{ base: '3', md: '4' }}
              flexDir="column"
            >
              <RCarGridCard
                isResults={true}
                backgroundPreload
                playersParticipant={comparisonPlayer}
                livery={comparisonPlayer?.car?.livery as Livery}
                appliedLivery={comparisonPlayer?.car?.appliedLivery as AppliedLivery}
                h="180px"
                imageProps={{
                  transform: 'scale(70%)',
                  marginBottom: '-1rem',
                }}  
                w="full"
                hasCardBg
                hasInsetBorder={false}
                style={{
                  backgroundOrigin: 'border-box',
                }}
              >
                <RBaseCardTopLeft>
                  <Flex
                    flexDirection="column"
                    gap="10px"
                    justifyContent="center"
                  >
                    <Flex alignItems="flex-start" gap="1" flexDir="column" mt="0.5rem">
                      <Text 
                        fontSize={{ base: '12px', md: '18px' }}
                        lineHeight={{ base: '16px', md: '24px' }}
                        fontFamily="heading" 
                        fontWeight="400"
                        color="white.95"
                      >
                        {comparisonPlayer?.car?.name}
                      </Text>
                      <Flex alignItems="center" gap="1">
                        <Box>
                          <RaceLevel
                            level={rarityLevels[(comparisonPlayer?.car?.rarity as RarityTier) as keyof typeof rarityLevels] || rarityLevels.common}
                            textFontSize="8px"
                            numberFontSize="12px"
                            minW="fit-content"
                          />
                        </Box>
                      </Flex>
                    </Flex>
                  </Flex>
                </RBaseCardTopLeft>
                <RBaseCardTopRight maxW="50%" flexDir="column">
                  <Flex
                    flexDir="column"
                    alignItems="center"
                    flex={1}
                  >
                    <Text
                      fontSize="0.625rem"
                      lineHeight="0.75rem"
                      fontWeight={600}
                      color="white.80"
                      textTransform="uppercase"
                      textAlign="center"
                    >
                      EARNINGS
                    </Text>
                    <Flex
                      background="linear-gradient(360deg, rgba(79, 197, 186, 0.2) 0%, rgba(79, 197, 186, 0) 100%)"
                      borderRadius="4px"
                      px="3"
                      py="2"
                      borderTop="none"
                      position="relative"
                      w="80px"
                      justifyContent="center"
                      _before={{
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "4px",
                        padding: "2px",
                        paddingTop: "0px",
                        borderWidth: "0px 1.5px 1.5px 1.5px",
                        background: "linear-gradient(360deg, rgba(79, 197, 186, 0.48) 0%, rgba(79, 197, 186, 0) 100%)",
                        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                        maskComposite: "exclude",
                      }}
                    >
                      <Text
                        fontSize="20px"
                        lineHeight="24px"
                        fontWeight={600}
                        color="treasuredTeal.100"
                      >
                        ${(comparisonPlayer?.earnings || 0) / 100}
                      </Text>
                    </Flex>
                  </Flex>

                  <Flex
                    flexDir="column"
                    alignItems="center"
                    flex={1}
                  >
                    <Text
                      fontSize="0.625rem"
                      lineHeight="0.75rem"
                      fontWeight={600}
                      color="white.80"
                      textTransform="uppercase"
                      textAlign="center"
                    >
                      POINTS
                    </Text>
                    <Flex
                      background="linear-gradient(360deg, rgba(216, 56, 50, 0.2) 0%, rgba(216, 56, 50, 0) 100%)"
                      borderRadius="4px"
                      px="3"
                      py="2"
                      borderTop="none"
                      position="relative"
                      w="80px"
                      justifyContent="center"
                      _before={{
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "4px",
                        padding: "2px",
                        paddingTop: "0px",
                        borderWidth: "0px 1.5px 1.5px 1.5px",
                        background: "linear-gradient(360deg, rgba(216, 56, 50, 0.48) 0%, rgba(216, 56, 50, 0) 100%)",
                        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                        maskComposite: "exclude",
                      }}
                    >
                      <Text
                        fontSize="20px"
                        lineHeight="24px"
                        fontWeight={600}
                        color="bloodMoon.100"
                      >
                        {comparisonPlayer?.points || 0}
                      </Text>
                    </Flex>
                  </Flex>
                </RBaseCardTopRight>
                <RBaseCardBottomRight>
                  <Flex alignItems="center" h="1.5rem" gap="2">
                    <Flags
                      countryCode={
                        player?.user
                          ?.countryFlag as FlagsProps['countryCode'] ||
                        'default'
                      }
                    />
                    <AvatarPicture
                      key={comparisonPlayer?.user.avatarId}
                      avatarId={comparisonPlayer?.user.avatarId || ''}
                    />
                    <Text 
                      fontSize="12.5px"
                      lineHeight="120%"
                      fontFamily="heading" 
                      fontWeight="400"
                      color="white.95"
                      noOfLines={1}
                    >
                      {comparisonPlayer?.user?.playerName || 'Owner Name'}
                    </Text>
                  </Flex>
                </RBaseCardBottomRight>
                <RBaseCardBottomLeft alignItems="center">
                  <Flex
                    flexDirection={{ base: 'column', md: 'row' }}
                    alignItems="center"
                    gap={4}
                    zIndex={2}
                    background="linear-gradient(225deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
                    borderTopRightRadius="12px"
                    borderTopLeftRadius="2px"
                    borderBottomLeftRadius="12px"
                    borderBottomRightRadius="2px"
                    px="2"
                    py="1.5"
                  >
                    <IconWatch
                    />
                  </Flex>

                  <Flex
                    flexDir="column"
                    alignItems="flex-start"
                  >
                    <Text
                      fontSize="0.75rem"
                      lineHeight="100%"
                      fontFamily="heading" 
                      fontWeight="400"
                      color="white.80"
                    >
                      TIME
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      lineHeight="0.875rem"
                      fontWeight="600"
                      color="white.60"
                    >
                      {comparisonPlayer?.time || '00:00:00'}
                    </Text>
                  </Flex>
                </RBaseCardBottomLeft>
              </RCarGridCard>
              <Flex mt={2} alignItems="center" justifyContent="center" gap="2">
                {getTyreIcon((comparisonPlayer?.tyres?.id as Tyres) || 'C1')}

                <Text
                  fontSize={{ base: '1.125rem', md: '1.5rem' }}
                  lineHeight={{ base: '1.5rem', md: '1.75rem' }}
                  fontWeight={400}
                  color="white.80"
                  fontFamily="heading"
                >
                  {comparisonPlayer?.tyres?.name}
                </Text>

                {comparisonPlayer?.user?.id === getUserSettings?.data?.getUser.id && (
                  <CustomTooltip
                    label={tipsForTyre}
                    placement="top"
                    hasArrow
                    gutter={8}
                    isOpen={isOpen}
                  >
                    <Box 
                      display="inline-block"
                      onMouseEnter={onOpen}
                      onMouseLeave={onClose}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen();
                      }}
                    >
                      <IconInfo 
                        __css={{
                          path: {
                            fill: 'white.80'
                          }
                        }}
                      />
                    </Box>
                  </CustomTooltip>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
